import { coerceDateTime } from '../my-journeys/myJourneysModel';
import { memberDisplayNameWithId } from '../member-display/memberDisplayModel';
import {
  isSnowflakeId,
  situationSeverityLabel,
  situationStatusLabel,
  situationTypeLabel,
} from '../messages/renderers/travelCardModel';

export const SITUATION_TYPES = Object.freeze([
  'FLIGHT_DELAY', 'FLIGHT_CANCELLED', 'ACCOMMODATION', 'RESTAURANT', 'TRANSPORT',
  'WEATHER', 'HEALTH', 'LOST_ITEM', 'MEETING', 'SAFETY', 'EMERGENCY', 'OTHER',
]);

export const SITUATION_SEVERITIES = Object.freeze([
  'INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL',
]);

export const SITUATION_STATUSES = Object.freeze(['OPEN', 'RESOLVED', 'CANCELLED']);

export const WRITABLE_SITUATION_LIFECYCLES = Object.freeze(['READY', 'TRAVELING']);

export const COLLABORATOR_ROLES = Object.freeze(['OWNER', 'COORDINATOR', 'MEMBER']);

const SEVERITY_RANK = Object.freeze({
  CRITICAL: 5,
  HIGH: 4,
  MEDIUM: 3,
  LOW: 2,
  INFO: 1,
});

export function asIdString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function situationDetailRoute(journeyId, situationId) {
  const jid = asIdString(journeyId);
  const sid = asIdString(situationId);
  if (!isSnowflakeId(jid) || !isSnowflakeId(sid)) return '';
  return `/journeys/${jid}/situations/${sid}`;
}

export function createSituationClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sit-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function canCreateSituation({ role, lifecycleStatus }) {
  return COLLABORATOR_ROLES.includes(role)
    && WRITABLE_SITUATION_LIFECYCLES.includes(lifecycleStatus);
}

export function canMutateSituation({
  role, lifecycleStatus, situationStatus, createdByMemberId, viewerMemberId,
}) {
  if (!WRITABLE_SITUATION_LIFECYCLES.includes(lifecycleStatus)) return false;
  if (situationStatus !== 'OPEN') return false;
  if (role === 'OWNER' || role === 'COORDINATOR') return true;
  return createdByMemberId != null
    && viewerMemberId != null
    && String(createdByMemberId) === String(viewerMemberId);
}

export function classifySituationError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || '');
  if (code === 94002 || key === 'travel.collaboration.situation.not.found') return 'not-found';
  if (key === 'travel.journey.not.found' || key.includes('not.found')) return 'not-found';
  if (code === 94001 || key === 'travel.collaboration.permission.denied') return 'forbidden';
  if (key === 'travel.collaboration.situation.concurrent.modification') return 'conflict';
  if (key === 'travel.collaboration.situation.idempotency.conflict') return 'idempotency';
  if (key === 'travel.collaboration.situation.invalid.lifecycle') return 'invalid-transition';
  if (key === 'travel.collaboration.lifecycle.not.writable') return 'lifecycle';
  if (code === 94000 || key === 'travel.collaboration.validation.failed') return 'validation';
  if (code === 90005 || key.includes('token.expired')) return 'unauthorized';
  return 'error';
}

export function situationErrorMessage(cause, fallback) {
  const kind = classifySituationError(cause);
  const detail = cause?.raw?.message || cause?.message || '';
  if (kind === 'validation') return detail || '請檢查旅程狀況內容。';
  if (kind === 'forbidden') return '你沒有權限執行此操作。';
  if (kind === 'not-found') return '找不到這個旅程狀況。';
  if (kind === 'conflict') return '這個旅程狀況剛剛有新的變更。請重新載入後再試。';
  if (kind === 'idempotency') return '這個建立請求與既有紀錄衝突。';
  if (kind === 'invalid-transition') return '目前狀態不允許此操作。';
  if (kind === 'lifecycle') return '目前旅程狀態不允許變更旅程狀況。';
  if (kind === 'unauthorized') return 'Session expired. Please sign in again.';
  return detail || fallback || '旅程狀況操作失敗。';
}

export function normalizeSituation(value) {
  const situationId = asIdString(value?.situationId);
  const journeyId = asIdString(value?.journeyId);
  const situationStatus = String(value?.situationStatus || '');
  const severity = String(value?.severity || '');
  const situationType = String(value?.situationType || '');
  const assignedToMemberId = value?.assignedToMemberId == null ? '' : asIdString(value.assignedToMemberId);
  return {
    situationId,
    journeyId,
    createdByMemberId: asIdString(value?.createdByMemberId),
    situationType,
    situationTypeLabel: situationType ? situationTypeLabel(situationType) : '',
    severity,
    severityLabel: severity ? situationSeverityLabel(severity) : '',
    situationStatus,
    situationStatusLabel: situationStatusLabel(situationStatus),
    title: String(value?.title || '').trim() || '未命名狀況',
    description: value?.description == null ? '' : String(value.description),
    linkedActivityId: value?.linkedActivityId == null ? '' : asIdString(value.linkedActivityId),
    assignedToMemberId,
    source: value?.source || '',
    detectedAt: coerceDateTime(value?.detectedAt) || '',
    resolvedAt: coerceDateTime(value?.resolvedAt) || '',
    resolvedByMemberId: value?.resolvedByMemberId == null ? '' : asIdString(value.resolvedByMemberId),
    resolutionNote: value?.resolutionNote || '',
    cancelledAt: coerceDateTime(value?.cancelledAt) || '',
    cancelledByMemberId: value?.cancelledByMemberId == null ? '' : asIdString(value.cancelledByMemberId),
    cancellationReason: value?.cancellationReason || '',
    rowVersion: value?.rowVersion ?? null,
    linkedDecisionCount: value?.linkedDecisionCount ?? null,
    replay: Boolean(value?.replay),
    createTime: coerceDateTime(value?.createTime) || '',
    updateTime: coerceDateTime(value?.updateTime) || '',
    detailRoute: situationDetailRoute(journeyId, situationId),
  };
}

export function unwrapSituationList(payload) {
  if (Array.isArray(payload)) return payload.map(normalizeSituation);
  if (Array.isArray(payload?.items)) return payload.items.map(normalizeSituation);
  return [];
}

export function partitionSituationList(items) {
  const open = [];
  const resolved = [];
  const cancelled = [];
  const other = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item.situationStatus === 'OPEN') open.push(item);
    else if (item.situationStatus === 'RESOLVED') resolved.push(item);
    else if (item.situationStatus === 'CANCELLED') cancelled.push(item);
    else other.push(item);
  });
  return { open, resolved, cancelled, other };
}

export function situationsNeedingAttention(items) {
  const open = (Array.isArray(items) ? items : []).filter((item) => item?.situationStatus === 'OPEN');
  return [...open].sort((left, right) => {
    const rankDiff = (SEVERITY_RANK[right.severity] || 0) - (SEVERITY_RANK[left.severity] || 0);
    if (rankDiff !== 0) return rankDiff;
    return String(right.detectedAt || '').localeCompare(String(left.detectedAt || ''));
  });
}

export function assigneeLabel(memberId, summariesById) {
  if (!memberId) return '';
  return memberDisplayNameWithId(memberId, summariesById);
}

export function activeCollaboratorOptions(members, summariesById) {
  return (Array.isArray(members) ? members : [])
    .filter((member) => member.membershipStatus === 'ACTIVE' && COLLABORATOR_ROLES.includes(member.role))
    .map((member) => ({
      memberId: asIdString(member.memberId),
      label: memberDisplayNameWithId(member.memberId, summariesById),
    }));
}

export function buildCreateSituationBody(draft, { clientRequestId } = {}) {
  const body = {
    clientRequestId: String(clientRequestId || '').trim(),
    situationType: draft.situationType,
    title: String(draft.title || '').trim(),
    confirm: true,
  };
  if (draft.severity) body.severity = draft.severity;
  const description = String(draft.description || '').trim();
  if (description) body.description = description;
  return body;
}

export function buildUpdateSituationBody(draft, expectedSituationRowVersion) {
  return {
    expectedSituationRowVersion,
    title: String(draft.title || '').trim(),
    description: String(draft.description || '').trim() || null,
    severity: draft.severity,
    confirm: true,
  };
}

export function buildAssignSituationBody(assignedToMemberId, expectedSituationRowVersion) {
  const body = {
    expectedSituationRowVersion,
    confirm: true,
  };
  const id = String(assignedToMemberId || '').trim();
  body.assignedToMemberId = id ? id : null;
  return body;
}

export function buildResolveSituationBody(resolutionNote, expectedSituationRowVersion) {
  const note = String(resolutionNote || '').trim();
  return {
    expectedSituationRowVersion,
    confirm: true,
    ...(note ? { resolutionNote: note } : {}),
  };
}

export function buildCancelSituationBody(reason, expectedSituationRowVersion) {
  return {
    expectedSituationRowVersion,
    reason: String(reason || '').trim(),
    confirm: true,
  };
}
